import { useState } from 'react';
import { PermissionGuard } from '@/features/auth';
import { useAssignableGroupsForDealerQuery } from '@/features/security-admin/hooks/useAssignableGroupsForDealerQuery';
import { SecurityGroupPermissionsPicker } from '@/features/security-admin/components/SecurityGroupPermissionsPicker';
import type {
  AssignUserToGroupsPayload,
  GroupPermissionTriple,
} from '@/features/security-admin/types/security-admin.types';
import { Alert } from '@/shared/components';

const MAX_GROUPS = 200;
const EMPTY_DEFAULTS: GroupPermissionTriple = { can_quote: false, can_invoice: false, can_sell: false };

interface Props {
  dealerId: string | null;
  onSelectionChange: (selection: AssignUserToGroupsPayload | null) => void;
}

/** Optional security-group picker, visible only once a home dealer is selected. */
export function UserSecurityGroupField({ dealerId, onSelectionChange }: Props) {
  if (dealerId === null) return null;

  return (
    <PermissionGuard permission={{ resource: 'security_group', action: 'manage_users' }}>
      <UserSecurityGroupFieldBody dealerId={dealerId} onSelectionChange={onSelectionChange} />
    </PermissionGuard>
  );
}

function UserSecurityGroupFieldBody({ dealerId, onSelectionChange }: { dealerId: string } & Pick<Props, 'onSelectionChange'>) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [defaults, setDefaults] = useState<GroupPermissionTriple>(EMPTY_DEFAULTS);
  const [defaultsTouched, setDefaultsTouched] = useState(false);

  const query = useAssignableGroupsForDealerQuery({ dealerId });
  const groups = query.data?.data ?? [];

  const emit = (ids: Set<string>, touched: boolean, values: GroupPermissionTriple) => {
    if (ids.size === 0) {
      onSelectionChange(null);
      return;
    }
    onSelectionChange(
      touched ? { group_ids: Array.from(ids), default_permissions: values } : { group_ids: Array.from(ids) },
    );
  };

  const toggleGroup = (id: string, checked: boolean) => {
    const next = new Set(selectedIds);
    if (checked) next.add(id);
    else next.delete(id);
    setSelectedIds(next);
    emit(next, defaultsTouched, defaults);
  };

  const toggleDefault = (key: keyof GroupPermissionTriple, value: boolean) => {
    const next = { ...defaults, [key]: value };
    setDefaults(next);
    setDefaultsTouched(true);
    emit(selectedIds, true, next);
  };

  return (
    <div className="space-y-2">
      <span>
        Security Groups <span className="text-mp-muted">(optional)</span>
      </span>

      {query.isLoading ? (
        <p className="text-sm text-mp-muted">Loading security groups...</p>
      ) : query.isError ? (
        <Alert variant="error">
          <span>Failed to load security groups for this dealer.</span>
        </Alert>
      ) : (
        <SecurityGroupPermissionsPicker
          groups={groups}
          selectedIds={selectedIds}
          onToggleGroup={toggleGroup}
          defaults={defaults}
          onToggleDefault={toggleDefault}
          maxGroups={MAX_GROUPS}
          emptyMessage="No security groups are assignable to this dealer."
        />
      )}
    </div>
  );
}
